
import React, { useMemo } from 'react';
import { Info, CheckCircle, Minus, AlertTriangle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

interface Filing {
  return_type: string;
  date_of_filing: string;
  financial_year: string;
  tax_period: string;
  status: string;
}

interface GSTDetails {
  gstin: string;
  status: string;
  company_name: string;
  trade_name: string;
  state: string;
  filings: Filing[];
}

interface ConsolidatedComplianceCalendarProps {
  gstDetails: GSTDetails[];
  returnType: string;
}

export const ConsolidatedComplianceCalendar: React.FC<ConsolidatedComplianceCalendarProps> = ({ 
  gstDetails, 
  returnType 
}) => {
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const getDueDate = (taxPeriod: string, financialYear: string, returnType: string): Date => {
    const monthMap: { [key: string]: number } = {
      'January': 0, 'February': 1, 'March': 2, 'April': 3, 'May': 4, 'June': 5,
      'July': 6, 'August': 7, 'September': 8, 'October': 9, 'November': 10, 'December': 11
    };
    
    const month = monthMap[taxPeriod];
    if (month === undefined) return new Date();
    
    const [startYear] = financialYear.split('-').map(y => parseInt(y));
    if (isNaN(startYear)) return new Date();
    
    // For financial year 2023-24, April 2023 to March 2024
    const adjustedYear = month >= 3 ? startYear : startYear + 1;
    
    const dueDay = returnType === 'GSTR1' ? 11 : 20;
    return new Date(adjustedYear, month + 1, dueDay);
  };

  const getDelayDays = (filing: Filing): number => {
    const filingDate = new Date(filing.date_of_filing);
    const dueDate = getDueDate(filing.tax_period, filing.financial_year, filing.return_type);
    
    if (isNaN(filingDate.getTime()) || isNaN(dueDate.getTime())) return 0;
    if (filingDate <= dueDate) return 0;
    
    const diffTime = filingDate.getTime() - dueDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getFinancialYears = useMemo(() => {
    const years = new Set<string>();
    gstDetails.forEach(company => {
      company.filings.forEach(filing => {
        if (filing.return_type === returnType) {
          years.add(filing.financial_year);
        }
      });
    });
    // Sort years with latest first, then slice to show last 5 years
    return Array.from(years).sort((a, b) => b.localeCompare(a)).slice(0, 5);
  }, [gstDetails, returnType]);

  const getFilingsForPeriod = (month: string, year: string) => {
    const monthMap: { [key: string]: string } = {
      'Jan': 'January', 'Feb': 'February', 'Mar': 'March', 'Apr': 'April', 
      'May': 'May', 'Jun': 'June', 'Jul': 'July', 'Aug': 'August', 
      'Sep': 'September', 'Oct': 'October', 'Nov': 'November', 'Dec': 'December'
    };
    
    const allFilings: Array<Filing & { gstin: string; state: string; company_name: string }> = [];
    
    gstDetails.forEach(company => {
      const filing = company.filings.find(filing => 
        filing.tax_period === monthMap[month] && 
        filing.financial_year === year && 
        filing.return_type === returnType
      );
      
      if (filing) {
        allFilings.push({
          ...filing,
          gstin: company.gstin,
          state: company.state,
          company_name: company.company_name
        });
      }
    });
    
    return allFilings;
  };

  const getTotalDelays = (delays: number[]): number => {
    return delays.reduce((total, delay) => total + delay, 0);
  };

  const getRiskLevel = (delays: number[]): 'low' | 'medium' | 'high' => {
    if (delays.length === 0) return 'low';
    
    const totalDelay = getTotalDelays(delays);
    const maxDelay = Math.max(...delays);
    
    // Risk assessment based on total impact and severity
    if (totalDelay >= 100 || maxDelay >= 30) return 'high';
    if (totalDelay >= 50 || maxDelay >= 15) return 'medium';
    return 'low';
  };

  const renderConsolidatedCell = useMemo(() => (month: string, year: string) => {
    const filings = getFilingsForPeriod(month, year);
    
    if (filings.length === 0) {
      return (
        <div className="flex justify-center items-center h-8 w-full">
          <Minus className="w-3 h-3 text-gray-400" />
        </div>
      );
    }

    const delays = filings.map(filing => getDelayDays(filing));
    const onTimeCount = delays.filter(delay => delay === 0).length;
    const delayedFilings = filings.filter((_, index) => delays[index] > 0);
    const totalDelays = getTotalDelays(delays);
    const riskLevel = getRiskLevel(delays);

    // All filings are on time
    if (onTimeCount === filings.length) {
      const tooltipContent = (
        <div className="space-y-2 max-w-sm">
          <div className="font-semibold">All On-Time ({filings.length})</div>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {filings.map(filing => (
              <div key={filing.gstin} className="text-xs border-b border-gray-200 pb-1">
                <div className="font-medium">{filing.gstin}</div>
                <div className="text-gray-500">{filing.state} • {filing.company_name}</div>
              </div>
            ))}
          </div>
        </div>
      );

      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex justify-center items-center h-8 w-full cursor-pointer">
                <CheckCircle className="w-4 h-4 text-green-500" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-sm bg-white">
              {tooltipContent}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    // Mixed or all delayed - show total delays
    const bgColor = riskLevel === 'high' ? 'bg-red-500' : 
                   riskLevel === 'medium' ? 'bg-orange-500' : 'bg-yellow-500';
    
    const tooltipContent = (
      <div className="space-y-2 max-w-sm background-white">
        <div className="max-h-32 overflow-y-auto space-y-1">
          {delayedFilings.map(filing => {
            const delay = getDelayDays(filing);
            return (
              <div key={filing.gstin} className="text-xs border-b border-gray-200 pb-1">
                <div className="font-medium">{filing.gstin}</div>
                <div className="text-gray-500">{filing.state} • {filing.company_name}</div>
                <div className="text-red-300">Delayed by {delay} days</div>
              </div>
            );
          })}
          {onTimeCount > 0 && (
            <div className="text-xs text-gray-500 mt-2">
              {onTimeCount} entities filed on time
            </div>
          )}
        </div>
      </div>
    );

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex justify-center items-center h-8 w-full cursor-pointer">
              <div className={`w-5 h-5 rounded-full ${bgColor} flex items-center justify-center relative text-xs`}>
                <span className="text-white font-semibold">{totalDelays}</span>
                {riskLevel === 'high' && (
                  <AlertTriangle className="w-2 h-2 text-white absolute -top-1 -right-1" />
                )}
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-sm bg-white">
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }, [gstDetails, returnType]);

  // Calculate consolidated stats without risk-weighted score
  const consolidatedStats = useMemo(() => {
    const allFilings = gstDetails.flatMap(company => 
      company.filings.filter(filing => filing.return_type === returnType)
    );
    
    const totalFilings = allFilings.length;
    const onTimeFilings = allFilings.filter(filing => getDelayDays(filing) === 0).length;
    const onTimePercentage = totalFilings > 0 ? Math.round((onTimeFilings / totalFilings) * 100) : 0;
    
    const delayedFilings = allFilings.filter(filing => getDelayDays(filing) > 0);
    const totalDelaysSum = delayedFilings.reduce((sum, filing) => sum + getDelayDays(filing), 0);
    const avgDelay = delayedFilings.length > 0 ? totalDelaysSum / delayedFilings.length : 0;
    
    return {
      onTimePercentage,
      totalDelaysSum,
      avgDelay
    };
  }, [gstDetails, returnType]);

  const financialYears = getFinancialYears;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Consolidated {returnType === 'GSTR1' ? 'GSTR-1' : 'GSTR-3B'} Filing Status
          </h3>
          <Info className="w-4 h-4 text-gray-400" />
        </div>
      </div>

      {/* Updated Stats - Removed Risk-Weighted Score */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <div className="text-xl font-bold text-gray-900">{consolidatedStats.onTimePercentage}%</div>
          <div className="text-xs text-gray-500">On-Time Filings</div>
        </div>
        <div>
          <div className="text-xl font-bold text-red-600">{consolidatedStats.totalDelaysSum}</div>
          <div className="text-xs text-gray-500">Total Delays (days)</div>
        </div>
        <div>
          <div className="text-xl font-bold text-orange-600">{gstDetails.length}</div>
          <div className="text-xs text-gray-500">Total GSTINs</div>
        </div>
      </div>

      {/* Enhanced Legend */}
      <div className="flex items-center gap-4 mb-4 text-xs flex-wrap">
        <div className="flex items-center gap-1">
          <Minus className="w-3 h-3 text-gray-400" />
          <span className="text-gray-600">No Data</span>
        </div>
        <div className="flex items-center gap-1">
          <CheckCircle className="w-3 h-3 text-green-500" />
          <span className="text-gray-600">All On-Time</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-yellow-500 flex items-center justify-center">
            <span className="text-white text-[8px]">Σ</span>
          </div>
          <span className="text-gray-600">Low Risk</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-orange-500 flex items-center justify-center">
            <span className="text-white text-[8px]">Σ</span>
          </div>
          <span className="text-gray-600">Medium Risk</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-red-500 relative flex items-center justify-center">
            <span className="text-white text-[8px]">Σ</span>
            <AlertTriangle className="w-2 h-2 text-white absolute -top-0.5 -right-0.5" />
          </div>
          <span className="text-gray-600">High Risk</span>
        </div>
      </div>

      {/* Calendar Grid - Years now sorted with latest first */}
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse" style={{ minWidth: '600px' }}>
          <thead>
            <tr>
              <th className="text-left p-1 border-b border-gray-200 w-12 text-xs font-medium text-gray-600">Year</th>
              {months.map((month) => (
                <th key={month} className="text-center p-1 border-b border-gray-200 text-xs font-medium text-gray-600" style={{ width: '40px' }}>
                  {month}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {financialYears.map((year) => (
              <tr key={year} className="border-b border-gray-100 last:border-b-0">
                <td className="p-1 text-left">
                  <span className="text-xs font-semibold text-gray-900">
                    {year.split('-')[0]}
                  </span>
                </td>
                {months.map((month) => (
                  <td key={`${year}-${month}`} className="p-1 text-center border-l border-gray-100 first:border-l-0" style={{ width: '40px' }}>
                    {renderConsolidatedCell(month, year)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
